/**
 * Patient payments: deposits paid, refunds and totals.
 *
 * Replaces `renderPatientTransactions`, `switchPatientTransactionsTab` and
 * `viewRefundReceipt`. Every figure is derived from the bookings themselves —
 * the system has no separate ledger, which is why the legacy view reconstructed
 * the same numbers inline.
 */

import { useMemo, useState } from 'react';
import {
  Badge,
  EmptyState,
  ErrorState,
  Icon,
  Modal,
  PageHeader,
  Panel,
  Spinner,
  StatCard,
  Tabs,
} from '../../components/common';
import { useAuth } from '../../context/AuthContext';
import { usePatientBookings } from '../../hooks/useBookings';
import { formatCurrency } from '../../domain/pricing';
import { isoToDisplayDate, formatTime12 } from '../../domain/dates';
import { typeLabel } from '../../domain/bookings';

const TABS = [
  { id: 'outgoing', label: 'المدفوعات', icon: 'arrow-up-circle' },
  { id: 'refunds', label: 'المستردات', icon: 'arrow-down-circle' },
];

export function PatientTransactions() {
  const { patientId } = useAuth();
  const { bookings, loading, error, reload } = usePatientBookings(patientId, { poll: false });

  const [tab, setTab] = useState('outgoing');
  const [receipt, setReceipt] = useState(null);

  const { outgoing, refunds, totals } = useMemo(() => {
    const paid = bookings.filter((b) => b.depositPaid && Number(b.depositAmount) > 0);
    const refunded = bookings.filter((b) => Number(b.refundAmount) > 0 || b.refundApproved);

    return {
      outgoing: paid.sort((a, b) => String(b.date).localeCompare(String(a.date))),
      refunds: refunded.sort((a, b) => String(b.date).localeCompare(String(a.date))),
      totals: {
        deposits: paid.reduce((sum, b) => sum + (Number(b.depositAmount) || 0), 0),
        refunds: refunded.reduce((sum, b) => sum + (Number(b.refundAmount) || 0), 0),
        confirmed: paid.filter((b) => b.depositConfirmed).length,
        awaiting: paid.filter((b) => !b.depositConfirmed && !b.depositRejectionReason).length,
      },
    };
  }, [bookings]);

  const list = tab === 'outgoing' ? outgoing : refunds;

  const tabsWithCounts = TABS.map((t) => ({
    ...t,
    count: t.id === 'outgoing' ? outgoing.length : refunds.length,
  }));

  return (
    <div className="space-y-6">
      <PageHeader title="المعاملات المالية" subtitle="سجل العرابين والمستردات" icon="receipt" />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard icon="banknote" label="إجمالي العرابين" value={formatCurrency(totals.deposits)} accent="cyan" />
        <StatCard icon="undo-2" label="إجمالي المسترد" value={formatCurrency(totals.refunds)} accent="purple" />
        <StatCard icon="check-circle-2" label="عرابين مؤكدة" value={totals.confirmed} accent="emerald" />
        <StatCard icon="clock" label="بانتظار المراجعة" value={totals.awaiting} accent="amber" />
      </div>

      <Tabs tabs={tabsWithCounts} active={tab} onChange={setTab} />

      {error ? (
        <ErrorState error={error} onRetry={reload} />
      ) : loading && bookings.length === 0 ? (
        <Spinner label="جاري تحميل المعاملات..." />
      ) : list.length === 0 ? (
        <Panel>
          <EmptyState
            icon="receipt"
            title={tab === 'outgoing' ? 'لا توجد مدفوعات' : 'لا توجد مستردات'}
            message={
              tab === 'outgoing'
                ? 'ستظهر هنا العرابين التي تدفعها عند الحجز.'
                : 'ستظهر هنا المبالغ المستردة بعد إلغاء حجز مدفوع.'
            }
          />
        </Panel>
      ) : (
        <div className="space-y-3">
          {list.map((booking) => (
            <TransactionRow
              key={booking.id}
              booking={booking}
              kind={tab}
              onViewReceipt={() =>
                setReceipt(tab === 'outgoing' ? booking.depositReceipt : booking.refundReceipt)
              }
            />
          ))}
        </div>
      )}

      <Modal
        open={Boolean(receipt)}
        onClose={() => setReceipt(null)}
        title="الإيصال"
        icon="receipt"
        size="md"
      >
        {receipt ? (
          <img src={receipt} alt="إيصال" className="mx-auto max-h-[65vh] rounded-lg object-contain" />
        ) : (
          <EmptyState icon="image-off" title="لا يوجد إيصال" />
        )}
      </Modal>
    </div>
  );
}

function TransactionRow({ booking, kind, onViewReceipt }) {
  const isRefund = kind === 'refunds';
  const amount = isRefund ? booking.refundAmount : booking.depositAmount;
  const receipt = isRefund ? booking.refundReceipt : booking.depositReceipt;

  const state = isRefund
    ? booking.refundApproved
      ? { label: 'تم الاسترداد', className: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300' }
      : { label: 'قيد المراجعة', className: 'border-amber-500/30 bg-amber-500/10 text-amber-300' }
    : booking.depositConfirmed
      ? { label: 'مؤكد', className: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300' }
      : booking.depositRejectionReason
        ? { label: 'مرفوض', className: 'border-red-500/30 bg-red-500/10 text-red-300' }
        : { label: 'بانتظار المراجعة', className: 'border-amber-500/30 bg-amber-500/10 text-amber-300' };

  return (
    <Panel className="flex flex-wrap items-center gap-4">
      <span
        className={`rounded-xl p-3 ${
          isRefund ? 'bg-purple-500/10 text-purple-400' : 'bg-cyan-500/10 text-cyan-400'
        }`}
      >
        <Icon name={isRefund ? 'arrow-down-circle' : 'arrow-up-circle'} size={20} />
      </span>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="truncate font-bold text-white">{booking.doctorName || 'طبيب'}</h3>
          <Badge className={state.className}>{state.label}</Badge>
        </div>
        <p className="mt-0.5 text-xs text-slate-400">
          {typeLabel(booking.type)} — {isoToDisplayDate(booking.date) || booking.date} —{' '}
          {formatTime12(booking.time)}
        </p>
        {booking.depositRejectionReason && !isRefund && (
          <p className="mt-1 text-[11px] text-red-300">
            سبب الرفض: {booking.depositRejectionReason}
          </p>
        )}
      </div>

      <div className="flex shrink-0 items-center gap-3">
        <span className={`text-base font-bold ${isRefund ? 'text-purple-300' : 'text-cyan-300'}`}>
          {isRefund ? '+' : '−'} {formatCurrency(amount)}
        </span>
        {receipt && (
          <button
            type="button"
            onClick={onViewReceipt}
            className="rounded-lg bg-white/5 p-2 text-slate-300 transition-colors hover:bg-white/10 hover:text-white"
            aria-label="عرض الإيصال"
          >
            <Icon name="eye" size={16} />
          </button>
        )}
      </div>
    </Panel>
  );
}

export default PatientTransactions;
